import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useRegime } from '../../contexts/RegimeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useChartTheme } from '../../theme/chartTheme';
import type { MarketAnalyticsSnapshot } from '../../types/marketAnalytics';
import { useAnalyticsSnapshotOverride } from './RegimeSnapshotBridge';

/** Breeden-Litzenberger risk-neutral density curve. */
const BreedenLitzenbergerChart: React.FC<{
  height?: number;
  snapshot?: MarketAnalyticsSnapshot | null;
}> = ({ height = 260, snapshot: snapshotProp }) => {
  const { snapshot: regimeSnapshot } = useRegime();
  const overrideSnapshot = useAnalyticsSnapshotOverride();
  const snapshot = snapshotProp ?? overrideSnapshot ?? regimeSnapshot;
  const { language } = useLanguage();
  const ct = useChartTheme();
  const ar = language === 'ar';

  const data = useMemo(
    () => (snapshot?.rnd_curve ?? []).map((p) => ({ strike: p.strike, density: p.density })),
    [snapshot?.rnd_curve],
  );

  if (!data.length) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
        {ar
          ? 'لا تتوفر كثافة مخاطرة محايدة — تحتاج سلسلة خيارات شراء كافية.'
          : 'Risk-neutral density unavailable — need more call quotes on chain.'}
      </div>
    );
  }

  const spot = snapshot?.gex.spot ?? 0;
  const rnd = snapshot?.rnd;

  return (
    <div>
      {rnd && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 12,
            marginBottom: 8,
            color: 'var(--txt-2)',
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>
            {ar ? 'أمام' : 'Forward'}: {rnd.forward.toFixed(2)} ({rnd.forward_pct >= 0 ? '+' : ''}
            {rnd.forward_pct.toFixed(2)}%)
          </span>
          <span>
            {ar ? 'الالتواء' : 'Skew'}: {rnd.skewness.toFixed(2)}
          </span>
          <span>
            {ar ? 'التفرطح' : 'Kurt'}: {rnd.kurtosis.toFixed(2)}
          </span>
          <span>
            {ar ? 'ذيل' : 'Tail'}: {rnd.tail_ratio.toFixed(2)}
          </span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid stroke={ct.grid} strokeDasharray="3 4" />
          <XAxis
            dataKey="strike"
            stroke={ct.border}
            axisLine={{ stroke: ct.border }}
            tickLine={false}
            tick={{ fill: ct.text, fontSize: 11, fontFamily: 'var(--font-mono)' }}
            tickFormatter={(v) => String(Math.round(v))}
          />
          <YAxis
            stroke={ct.border}
            axisLine={{ stroke: ct.border }}
            tickLine={false}
            tick={{ fill: ct.text, fontSize: 11, fontFamily: 'var(--font-mono)' }}
            width={42}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--elev)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              fontFamily: 'var(--font-ui)',
              boxShadow: 'var(--lift-2)',
            }}
            labelStyle={{ color: 'var(--txt)', fontWeight: 500 }}
            itemStyle={{ color: 'var(--txt-2)' }}
            formatter={(val: number) => [val.toExponential(3), ar ? 'كثافة' : 'density']}
            labelFormatter={(l) => `${ar ? 'سعر' : 'Strike'} ${l}`}
          />
          {spot > 0 && (
            <ReferenceLine
              x={spot}
              stroke="var(--warn)"
              strokeDasharray="3 4"
              label={{ value: 'Spot', fontSize: 11, fill: 'var(--warn)' }}
            />
          )}
          <Area
            type="monotone"
            dataKey="density"
            stroke="var(--mmec-regime-accent, #5b8def)"
            fill="var(--mmec-regime-accent-soft, rgba(91,141,239,0.2))"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
        {ar
          ? 'طريقة بريدن-ليتزنبرجر: كثافة مخاطرة محايدة من أسعار خيارات الشراء (عرض فقط).'
          : 'Breeden-Litzenberger RND from call mids — analytics display only.'}
      </p>
    </div>
  );
};

export default BreedenLitzenbergerChart;
